/**
 * Walker tag-suggestion + coverage helpers (PR Q4 · §Q4.6).
 *
 * Heuristic: a populated cell shares evidence with its row + column
 * peers. An empty cell sharing a row or column with a populated one is
 * a "suggested" cell — confidence drops with the number of unfilled
 * cells on the same row + column.
 *
 * `coverageScore` reports populated vs total cells (N×M matrix from
 * derived state/mode tag sets).
 */
import type { AtlasManifest, Surface } from '@atlas-manifest/types';

const DEFAULT_STATES = ['baseline', 'in-distress', 'thriving', 'building'];
const DEFAULT_MODES = ['observer', 'advisor', 'collaborator', 'steward'];

function tagsForPrefix(manifest: AtlasManifest, prefix: string, defaults: string[]): string[] {
  // Always include defaults; derive extra tags from entities so custom
  // axes extend (not replace) the canonical matrix.
  const found = new Set<string>(defaults);
  for (const e of manifest.entities) {
    for (const t of e.tags) {
      if (t.startsWith(`${prefix}:`)) {
        const suffix = t.slice(prefix.length + 1);
        if (suffix && suffix !== 'all') found.add(suffix);
      }
    }
  }
  return [...found].sort();
}

function cellId(state: string, mode: string): string {
  return `${state}:${mode}`;
}

function surfacesForCell(manifest: AtlasManifest, state: string, mode: string): Surface[] {
  return manifest.surfaces.filter((s) => {
    const entity = manifest.entities.find((e) => e.surfaces.includes(s.id));
    if (!entity) return false;
    const stateMatch = entity.tags.includes(`state:${state}`) || entity.tags.includes('state:all');
    const modeMatch = entity.tags.includes(`mode:${mode}`) || entity.tags.includes('mode:all');
    return stateMatch && modeMatch;
  });
}

export interface TagGapSuggestion {
  cellId: string;
  candidateSurfaces: string[];
  confidence: 'high' | 'med' | 'low';
  rationale: string;
}

export function suggestTagGaps(manifest: AtlasManifest): TagGapSuggestion[] {
  const states = tagsForPrefix(manifest, 'state', DEFAULT_STATES);
  const modes = tagsForPrefix(manifest, 'mode', DEFAULT_MODES);

  // Step 1 — score every cell.
  const populatedByCell = new Map<string, string[]>();   // cellId → surface ids
  for (const s of states) {
    for (const m of modes) {
      const surfaces = surfacesForCell(manifest, s, m);
      if (surfaces.length > 0) {
        populatedByCell.set(cellId(s, m), surfaces.map((x) => x.id));
      }
    }
  }
  if (populatedByCell.size === 0) return [];

  // Step 2 — for every empty cell, collect candidates from row + column peers.
  const out: TagGapSuggestion[] = [];
  for (const s of states) {
    for (const m of modes) {
      const id = cellId(s, m);
      if (populatedByCell.has(id)) continue;
      const candidates = new Set<string>();
      for (const otherMode of modes) {
        const peer = populatedByCell.get(cellId(s, otherMode));
        if (peer) peer.forEach((x) => candidates.add(x));
      }
      for (const otherState of states) {
        const peer = populatedByCell.get(cellId(otherState, m));
        if (peer) peer.forEach((x) => candidates.add(x));
      }
      if (candidates.size === 0) continue;
      // Confidence: how many peers exist?
      const rowPeers = modes.filter((mm) => mm !== m && populatedByCell.has(cellId(s, mm))).length;
      const colPeers = states.filter((ss) => ss !== s && populatedByCell.has(cellId(ss, m))).length;
      const total = rowPeers + colPeers;
      const confidence: TagGapSuggestion['confidence'] =
        total >= 3 ? 'high' : total === 2 ? 'med' : 'low';
      out.push({
        cellId: id,
        candidateSurfaces: [...candidates].sort(),
        confidence,
        rationale: `${rowPeers} row peer${rowPeers === 1 ? '' : 's'} + ${colPeers} column peer${colPeers === 1 ? '' : 's'} suggest this cell is reachable.`,
      });
    }
  }
  return out.sort((a, b) => a.cellId.localeCompare(b.cellId));
}

export interface CoverageScore {
  populated: number;
  total: number;
}

export function coverageScore(manifest: AtlasManifest): CoverageScore {
  const states = tagsForPrefix(manifest, 'state', DEFAULT_STATES);
  const modes = tagsForPrefix(manifest, 'mode', DEFAULT_MODES);
  let populated = 0;
  for (const s of states) {
    for (const m of modes) {
      if (surfacesForCell(manifest, s, m).length > 0) populated++;
    }
  }
  return { populated, total: states.length * modes.length };
}
